import React, { useState, useEffect } from 'react';
import '../styles/GeneralTerms.css';

const GeneralTerms: React.FC = () => {
  const [visibleSections, setVisibleSections] = useState<number[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const sections = document.querySelectorAll('.terms-section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            if (!visibleSections.includes(index)) {
              setVisibleSections(prev => [...prev, index]);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    sections.forEach((section, index) => {
      section.setAttribute('data-index', index.toString());
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, [visibleSections]);

  // Dark mode detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <div className="general-terms-page">
      {/* Header */}
      <header className="terms-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <img 
                src="/favicon-32x32-white.png" 
                alt="GordianRFQ Logo" 
                className="logo-icon"
              />
              <a href="/" className="logo-text">RFQ Agent</a>
            </div>
          </div>
          <nav className="header-nav">
            <button className="nav-link" onClick={() => window.location.href = '/'}>
              Home
            </button>
            <button className="nav-link" onClick={() => window.location.href = '/#pricing'}>
              Pricing
            </button>
            <button className="nav-link" onClick={() => window.location.href = '/#about'}>
              About
            </button>
            <button className="nav-link" onClick={() => window.location.href = '/#contact'}>
              Contact
            </button>
            <button className="login-btn" onClick={() => window.location.href = '/signin'}>
              Sign In
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="terms-main">
        <div className="terms-container">
          <h1 className="terms-title">General Terms and Conditions (GTC)</h1>
          
          <div className="terms-content">
            <div className={`terms-section ${visibleSections.includes(0) ? 'visible' : ''}`}>
              <h2>1. Scope of Validity</h2>
              <p>
                These General Terms and Conditions ("GTC") apply to all services, products, and software solutions provided by 
                Gordian Analytics SA, headquartered in Lausanne, Vaud, Switzerland (hereinafter referred to as the "Company"). 
                The Company provides services including, but not limited to, access to software-as-a-service (SaaS) solutions 
                based on artificial intelligence, particularly large language models (LLMs), as well as related consulting services. 
                Services may be delivered via the Company website or through direct agreements. These GTC govern all areas of 
                activity of Gordian Analytics SA and apply to all customer relationships unless otherwise agreed in writing.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(1) ? 'visible' : ''}`}>
              <h2>2. Conclusion of Contract</h2>
              <p>A contract is formed when a customer:</p>
              <ul>
                <li>Books and pays for a service via the Company's website;</li>
                <li>Accepts an offer or quote issued by the Company;</li>
                <li>Makes use of the Company's services, software, or consulting.</li>
              </ul>
            </div>

            <div className={`terms-section ${visibleSections.includes(2) ? 'visible' : ''}`}>
              <h2>3. Prices</h2>
              <p>
                Unless otherwise stated, all prices are in Swiss francs (CHF) and exclude applicable VAT. 
                The Company reserves the right to adjust prices at any time. The price at the time of booking 
                or order confirmation shall apply.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(3) ? 'visible' : ''}`}>
              <h2>4. Payment</h2>
              <p>
                For standard consulting services booked online, payment is required at the time of booking via 
                available online payment options. For custom or larger projects, the Company may issue an invoice 
                payable within 30 days of the invoice date. The Company reserves the right to require advance payment 
                at any time. If payment is not received on time, the customer will be in default without reminder 
                and liable for default interest at a rate of 5% p.a.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(4) ? 'visible' : ''}`}>
              <h2>5. Provision of Services</h2>
              <p>
                The Company fulfills its obligations by providing access to the agreed digital services or by 
                performing the consulting services as described at the time of contract formation. Unless otherwise 
                agreed, services are rendered remotely.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(5) ? 'visible' : ''}`}>
              <h2>6. Use of SaaS and Digital Tools</h2>
              <p>
                Customers granted access to software or AI tools receive a non-exclusive, non-transferable, 
                limited license to use the tools for internal business purposes. Resale, sublicensing, or 
                external distribution is prohibited unless expressly agreed.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(6) ? 'visible' : ''}`}>
              <h2>7. Customer Obligations</h2>
              <p>Customers agree to:</p>
              <ul>
                <li>Use any services or tools only within the scope granted;</li>
                <li>Provide accurate and complete information when required;</li>
                <li>Cooperate with the Company in good faith, including timely provision of necessary documents or access.</li>
              </ul>
              <p>
                Customers are responsible for safeguarding any login credentials and accept full responsibility 
                for all actions taken using their accounts.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(7) ? 'visible' : ''}`}>
              <h2>8. Intellectual Property</h2>
              <p>
                All intellectual property rights in services, tools, software, and content provided by the Company 
                remain with Gordian Analytics SA or its licensors. No transfer of rights occurs unless explicitly agreed.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(8) ? 'visible' : ''}`}>
              <h2>9. Confidentiality</h2>
              <p>
                Both parties agree to maintain confidentiality over all proprietary or sensitive information 
                exchanged in the context of the business relationship. This obligation survives the termination of the contract.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(9) ? 'visible' : ''}`}>
              <h2>10. Warranty and Liability</h2>
              <p>
                The Company makes no warranties regarding uninterrupted access or freedom from errors or viruses 
                in its software services. The customer uses such services at their own risk. To the maximum extent 
                permitted by law, liability for direct damages is limited to the amount paid by the customer for 
                the specific service. Liability for indirect or consequential damages is excluded.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(10) ? 'visible' : ''}`}>
              <h2>10.1 AI Output Disclaimer</h2>
              <p>
                The Company's software and consulting services may involve the use of artificial intelligence systems, 
                including large language models (LLMs). These tools generate outputs based on statistical patterns 
                and are not guaranteed to be accurate, complete, or legally or professionally reliable. The customer 
                acknowledges and agrees that:
              </p>
              <ul>
                <li>Outputs generated by AI may contain errors or biases;</li>
                <li>The Company is not liable for decisions made or actions taken based on AI-generated content;</li>
                <li>The customer remains fully responsible for verifying and validating AI outputs before relying on them for business or legal purposes.</li>
              </ul>
            </div>

            <div className={`terms-section ${visibleSections.includes(11) ? 'visible' : ''}`}>
              <h2>11. Refund and Termination Policy</h2>
              <p>
                Customers may cancel standard consulting bookings up to 24 hours before the scheduled time for a full refund. 
                Cancellations within 24 hours are non-refundable. For custom projects or SaaS access, the following applies:
              </p>
              <ul>
                <li>Termination by the customer must be communicated in writing;</li>
                <li>Work already performed will be billed proportionally;</li>
                <li>Any prepaid amounts for unused services may be refunded at the Company's discretion.</li>
              </ul>
              <p>The Company may terminate a contract with immediate effect if the customer breaches any material obligation under these GTC.</p>
            </div>

            <div className={`terms-section ${visibleSections.includes(12) ? 'visible' : ''}`}>
              <h2>12. Data Protection</h2>
              <p>
                Personal and business data collected by the Company is processed in accordance with Swiss data protection laws. 
                The customer consents to such processing for the purpose of fulfilling contractual obligations. 
                A separate Privacy Policy applies.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(13) ? 'visible' : ''}`}>
              <h2>13. Changes to the GTC</h2>
              <p>
                The Company may amend these GTC at any time. The current version is published on the Company's website 
                and becomes effective upon publication. For ongoing contracts, the version in effect at the time of 
                contract conclusion applies unless the customer accepts a newer version.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(14) ? 'visible' : ''}`}>
              <h2>14. Severability</h2>
              <p>
                Should any provision of these GTC be deemed invalid, the remaining provisions remain in full force. 
                The invalid clause will be replaced by one that most closely matches the economic intent.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(15) ? 'visible' : ''}`}>
              <h2>15. Applicable Law and Jurisdiction</h2>
              <p>
                These GTC are governed by Swiss law. The place of jurisdiction is Lausanne, Switzerland, 
                unless mandatory legal provisions dictate otherwise.
              </p>
            </div>

            <div className={`terms-section ${visibleSections.includes(16) ? 'visible' : ''}`}>
              <p className="copyright">© 2025 Gordian Analytics. All rights reserved.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GeneralTerms;
